/**
 * Twin-Matrix module-grid fitting — pure math, no canvas or DOM.
 *
 * The primary sticker QR has its top-right corner destroyed by design, so no
 * decoder can find it and no three-finder homography exists. What survives is
 * enough: the TL and BL finders, both timing lines, and the alignment pattern.
 * Fitting the sampling grid to those spec-fixed modules recovers the matrix from
 * a roughly-aligned camera warp.
 *
 * Geometry is per-sticker, not a constant — the server reports the outer QR's
 * module count with the patch, so a SKU that mints at a different version does
 * not need a client release.
 */

export const CELL = 8; // pixels per module in the canonical canvas
export const QUIET = 4; // quiet-zone modules — decoders reject QRs without one
export const SEARCH_MARGIN = 6; // modules captured beyond the guide

/**
 * Centre of the sole alignment pattern, which the QR spec fixes per version.
 * Versions 2-6 have exactly one, at (c, c).
 */
const ALIGNMENT_CENTER_BY_GRID = {
  33: 26, // Version 4 — current mint
  41: 34, // Version 6
};

const FINDER_SIZE = 7;
const DEFAULT_VOID_COLS = 14;

const FINDER_7X7 = [
  [1, 1, 1, 1, 1, 1, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 1, 1, 1, 0, 1],
  [1, 0, 1, 1, 1, 0, 1],
  [1, 0, 1, 1, 1, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 1, 1, 1, 1, 1, 1],
];

const ALIGNMENT_5X5 = [
  [1, 1, 1, 1, 1],
  [1, 0, 0, 0, 1],
  [1, 0, 1, 0, 1],
  [1, 0, 0, 0, 1],
  [1, 1, 1, 1, 1],
];

/** Modules whose value the QR spec fixes. The TR finder is excluded — that is the void. */
function buildAnchors(grid, alignmentCenter) {
  const anchors = [];
  const push = (r, c, expect) => {
    if (r < 0 || r >= grid || c < 0 || c >= grid) return;
    anchors.push({ r, c, expect });
  };

  for (let r = 0; r < FINDER_SIZE; r++) {
    for (let c = 0; c < FINDER_SIZE; c++) {
      push(r, c, FINDER_7X7[r][c]);
      push(grid - FINDER_SIZE + r, c, FINDER_7X7[r][c]);
    }
  }
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      push(alignmentCenter - 2 + r, alignmentCenter - 2 + c, ALIGNMENT_5X5[r][c]);
    }
  }
  for (let c = 8; c <= grid - 9; c++) push(6, c, c % 2 === 0 ? 1 : 0);
  for (let r = 8; r <= grid - 9; r++) push(r, 6, r % 2 === 0 ? 1 : 0);

  return anchors;
}

/** ECC 200 36×36. Solid L plus the 2×2-region alignment cross, and the clock tracks. */
const DM_GRID = 36;
const DM_SOLID_COLS = [0, 18];
const DM_SOLID_ROWS = [17, 35];
const DM_CLOCK_ROWS = [0, 18];
const DM_CLOCK_COLS = [17, 35];

function buildDmAnchors(grid) {
  const seen = new Set();
  const anchors = [];
  const push = (r, c, expect) => {
    const key = r * grid + c;
    if (seen.has(key)) return;
    seen.add(key);
    anchors.push({ r, c, expect });
  };
  for (const c of DM_SOLID_COLS) {
    for (let r = 0; r < grid; r++) push(r, c, 1);
  }
  for (const r of DM_SOLID_ROWS) {
    for (let c = 0; c < grid; c++) push(r, c, 1);
  }
  // Top clock and the dashed alignment row: even columns dark.
  for (const r of DM_CLOCK_ROWS) {
    for (let c = 0; c < grid; c++) push(r, c, c % 2 === 0 ? 1 : 0);
  }
  // Right clock and the dashed alignment column: even rows light.
  for (const c of DM_CLOCK_COLS) {
    for (let r = 0; r < grid; r++) push(r, c, r % 2 === 0 ? 0 : 1);
  }
  return anchors;
}

const geometryCache = new Map();

/** ECC 200 anchors for the minted 36×36 symbol. */
export function dmGeometry(grid = DM_GRID) {
  if (grid !== DM_GRID) {
    throw new Error(`Unsupported Data Matrix size ${grid} — minted size is ${DM_GRID}`);
  }
  const cached = geometryCache.get(`dm:${grid}`);
  if (cached) return cached;
  const geometry = {
    grid,
    symbol: "datamatrix",
    anchors: buildDmAnchors(grid),
  };
  geometryCache.set(`dm:${grid}`, geometry);
  return geometry;
}

/** QR grids are 21+4k. 36 is the Data Matrix SKU and is not a QR size. */
export function symbolGeometry(grid) {
  if (grid === DM_GRID) return dmGeometry(grid);
  return qrGeometry(grid);
}

/**
 * Spec-derived geometry for an outer QR of `grid` modules.
 * Cached because the anchor table is rebuilt otherwise on every frame.
 */
export function qrGeometry(grid) {
  const cached = geometryCache.get(grid);
  if (cached) return cached;

  const alignmentCenter = ALIGNMENT_CENTER_BY_GRID[grid];
  if (!alignmentCenter) {
    throw new Error(
      `Unsupported outer matrix size ${grid} — known: ${Object.keys(ALIGNMENT_CENTER_BY_GRID).join(", ")}`
    );
  }
  const geometry = {
    grid,
    alignmentCenter,
    // Distance between the TL and TR finder centres, in modules. Drives both the
    // scale of the finder-based warp and its plausibility score.
    finderSpan: grid - FINDER_SIZE,
    anchors: buildAnchors(grid, alignmentCenter),
  };
  geometryCache.set(grid, geometry);
  return geometry;
}

function voidBounds(missingModules, grid) {
  const cols = missingModules?.cols ?? missingModules?.modules?.[0]?.length ?? 0;
  return {
    row0: missingModules?.row0 ?? 0,
    col0: missingModules?.col0 ?? grid - (cols || DEFAULT_VOID_COLS),
    rows: missingModules?.rows ?? missingModules?.modules?.length ?? 0,
    cols,
  };
}

/**
 * Where to sample when fitting the grid. Modules inside the void are physically
 * destroyed on the sticker, so scoring them only adds noise.
 */
export function buildSamplePlan(missingModules, geometry) {
  const { grid, anchors } = geometry;
  const { row0, col0, rows, cols } = voidBounds(missingModules, grid);
  const inVoid = (r, c) => r >= row0 && r < row0 + rows && c >= col0 && c < col0 + cols;

  const modules = [];
  for (let r = 0; r < grid; r++) {
    for (let c = 0; c < grid; c++) {
      if (!inVoid(r, c)) modules.push({ r, c });
    }
  }
  return { anchors: anchors.filter((a) => !inVoid(a.r, a.c)), modules };
}

export function toGrayscale(imageData) {
  const { data, width, height } = imageData;
  const gray = new Uint8ClampedArray(width * height);
  for (let i = 0, g = 0; g < gray.length; i += 4, g++) {
    gray[g] = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
  }
  return gray;
}

export function otsuThreshold(values) {
  const hist = new Uint32Array(256);
  for (let i = 0; i < values.length; i++) {
    hist[Math.max(0, Math.min(255, Math.round(values[i])))]++;
  }
  const total = values.length;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];

  let sumB = 0;
  let wB = 0;
  let bestVariance = 0;
  let threshold = 128;
  for (let i = 0; i < 256; i++) {
    wB += hist[i];
    if (!wB) continue;
    const wF = total - wB;
    if (!wF) break;
    sumB += i * hist[i];
    const meanB = sumB / wB;
    const meanF = (sum - sumB) / wF;
    const variance = wB * wF * (meanB - meanF) * (meanB - meanF);
    if (variance > bestVariance) {
      bestVariance = variance;
      threshold = i;
    }
  }
  return threshold;
}

/** Fraction of spec-fixed modules that match when the image is sampled on this grid. */
export function anchorMatchRatio(gray, w, h, thr, plan, originX, originY, pitch) {
  const { anchors } = plan;
  if (!anchors.length) return 0;
  let hit = 0;
  for (const a of anchors) {
    const x = Math.round(originX + (a.c + 0.5) * pitch);
    const y = Math.round(originY + (a.r + 0.5) * pitch);
    if (x < 0 || x >= w || y < 0 || y >= h) continue;
    const dark = gray[y * w + x] < thr ? 1 : 0;
    if (dark === a.expect) hit++;
  }
  return hit / anchors.length;
}

/**
 * Continuous fit quality. A plain hit count plateaus — many grids score the same
 * and the search settles on one whose pitch is off by a percent, which then
 * drifts a full module away across the matrix. Scoring by signed distance from
 * the threshold instead rewards sampling deep inside modules rather than near
 * their edges, giving a sharp peak at the true origin and pitch.
 *
 * The contrast term covers every surviving module, so pitch is constrained by
 * the whole matrix rather than only the corners that carry anchors.
 */
export function scoreGrid(gray, w, h, thr, plan, originX, originY, pitch, stride = 1) {
  const { anchors, modules } = plan;

  let anchorSum = 0;
  let anchorN = 0;
  for (let i = 0; i < anchors.length; i += stride) {
    const a = anchors[i];
    anchorN++;
    const x = Math.round(originX + (a.c + 0.5) * pitch);
    const y = Math.round(originY + (a.r + 0.5) * pitch);
    if (x < 0 || x >= w || y < 0 || y >= h) {
      anchorSum -= 255;
      continue;
    }
    anchorSum += a.expect ? thr - gray[y * w + x] : gray[y * w + x] - thr;
  }

  let contrastSum = 0;
  let contrastN = 0;
  for (let i = 0; i < modules.length; i += stride) {
    const m = modules[i];
    contrastN++;
    const x = Math.round(originX + (m.c + 0.5) * pitch);
    const y = Math.round(originY + (m.r + 0.5) * pitch);
    if (x < 0 || x >= w || y < 0 || y >= h) continue;
    contrastSum += Math.abs(gray[y * w + x] - thr);
  }

  const anchorTerm = anchorN ? anchorSum / (anchorN * 255) : -1;
  const contrastTerm = contrastN ? contrastSum / (contrastN * 255) : 0;
  return anchorTerm + 0.5 * contrastTerm;
}

function refineFit(gray, w, h, thr, plan, center, offsetSpan, offsetStep, pitchSpan, pitchStep, stride) {
  let best = { ...center, score: -Infinity };
  const pitchMax = center.pitch * (1 + pitchSpan);
  const pitchDelta = center.pitch * pitchStep;

  for (let pitch = center.pitch * (1 - pitchSpan); pitch <= pitchMax + 1e-9; pitch += pitchDelta) {
    for (let dy = -offsetSpan; dy <= offsetSpan + 1e-9; dy += offsetStep) {
      for (let dx = -offsetSpan; dx <= offsetSpan + 1e-9; dx += offsetStep) {
        const originX = center.originX + dx * center.pitch;
        const originY = center.originY + dy * center.pitch;
        const score = scoreGrid(gray, w, h, thr, plan, originX, originY, pitch, stride);
        if (score > best.score) best = { originX, originY, pitch, score };
      }
    }
  }
  return best;
}

/**
 * Snap the sampling grid onto the QR's spec-fixed patterns.
 * Coarse-to-fine over origin and module pitch; the guide only supplies a seed.
 */
export function searchModuleGrid(gray, w, h, thr, plan, seedOrigin, seedPitch) {
  let fit = { originX: seedOrigin, originY: seedOrigin, pitch: seedPitch, score: -Infinity };
  fit = refineFit(gray, w, h, thr, plan, fit, 5, 1, 0.28, 0.04, 3);
  fit = refineFit(gray, w, h, thr, plan, fit, 1.5, 0.25, 0.06, 0.015, 2);
  fit = refineFit(gray, w, h, thr, plan, fit, 0.3, 0.05, 0.02, 0.004, 1);
  fit = refineFit(gray, w, h, thr, plan, fit, 0.08, 0.02, 0.005, 0.001, 1);
  return {
    ...fit,
    match: anchorMatchRatio(gray, w, h, thr, plan, fit.originX, fit.originY, fit.pitch),
  };
}

/** Read the module values by averaging the middle of each cell. */
export function sampleGridDark(gray, w, h, thr, originX, originY, pitch, grid) {
  const dark = new Uint8Array(grid * grid);
  const half = Math.max(0, Math.floor(pitch / 4));
  for (let my = 0; my < grid; my++) {
    for (let mx = 0; mx < grid; mx++) {
      const cx = Math.round(originX + (mx + 0.5) * pitch);
      const cy = Math.round(originY + (my + 0.5) * pitch);
      let sum = 0;
      let n = 0;
      for (let dy = -half; dy <= half; dy++) {
        const y = cy + dy;
        if (y < 0 || y >= h) continue;
        for (let dx = -half; dx <= half; dx++) {
          const x = cx + dx;
          if (x < 0 || x >= w) continue;
          sum += gray[y * w + x];
          n++;
        }
      }
      dark[my * grid + mx] = n && sum / n < thr ? 1 : 0;
    }
  }
  return dark;
}

export function applyMissingModules(dark, missingModules, grid) {
  if (!missingModules?.modules?.length) return;
  const { row0, col0, rows, cols } = voidBounds(missingModules, grid);

  for (let r = 0; r < rows; r++) {
    const row = missingModules.modules[r];
    if (!row) continue;
    for (let c = 0; c < cols; c++) {
      const gy = row0 + r;
      const gx = col0 + c;
      if (gy < 0 || gy >= grid || gx < 0 || gx >= grid) continue;
      dark[gy * grid + gx] = row[c] ? 1 : 0;
    }
  }
}

export { voidBounds };
